import { useState, useRef, useEffect, createContext, useContext } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "../i18n";
import { CT, RAD, FS, Z } from "../lib/tokens";
import { ModalLayout } from "./SharedUI";
import { withCount } from "../lib/pluralVars"; // 209 §7 — a sentence's number, read once, as its placeholder and as `count`

// Vite folds this at build time, and it is read through a FUNCTION rather than
// at module scope. That is not style: `recordings/57c-entitylist-freeze`
// evaluates this file in a `vm` context where `import.meta` is undefined, so a
// module-scope read made merely IMPORTING this module throw there — and §2 put
// it in that harness's require graph for the first time by adding the
// `useConfirm` import to CddClientSection. Optional chaining is the second
// guard, for any other sandbox that defines `import_meta` as undefined.
const isDev = () => !!(import.meta as any)?.env?.DEV;

// Mirror a prop/state value into a ref so closures can read the latest value
// instead of the value captured when they were created. Required by
// confirmAndUndo's restore closures: they are built at click time and stashed
// in hook state, so they cannot close over React props directly — by the time
// Undo fires (up to 5s later), the captured prop is stale and rebuilding the
// array from it would re-introduce the deleted item.
export function useRefLatest(value: any) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

// Single-slot confirmation + undo helper (UNDO_WINDOW_MS, 10 s since 209 §25;
// per-call override via `windowMs` — 140 §2 raised it to 20s for the convert site).
//
// Behaviour:
//   - Single-slot: a second delete within the window finalizes the first.
//     The prior mutation has already committed; only its reversal window
//     closes early — so a pending `onExpire` fires AT THAT MOMENT, not never.
//   - On unmount the pending undo auto-commits: the delete persists, the
//     toast state dies with the hook — and a pending `onExpire` fires, for
//     the same reason. `onExpire` has exactly two fates: it runs once (window
//     elapsed / closed early / unmount), or Undo was clicked and it never
//     runs. Nothing else.
//   - Dev-mode guard: calling with neither `label`/`message` nor `undo`
//     throws — that combination is a direct delete with no safety net and is
//     treated as a wiring bug.
//
// Call shape:
//   confirmAndUndo({
//     label,        // "this document" | null — null (and no message) skips the confirm modal
//     title,        // 140 §2 — custom dialog title (data, e.g. the row's ref); label-derived otherwise
//     message,      // 140 §2 — custom body sentence; makes it a PROCEED dialog (see ConfirmDeleteDialog)
//     confirmLabel, // 140 §2 — the proceed button's text (used with message)
//     onConfirm,    // () => void — performs the mutation
//     undo,         // { snapshot, restore } | null — null = permanent
//     toastName,    // "Removed \"foo.pdf\""
//     windowMs,     // undo window, default UNDO_WINDOW_MS; the toast's progress bar follows it
//     onExpire,     // () => void — the COMMIT point: the window closed without undo
//   })
//
// Snapshot/restore closures live at the call site: the hook is dumb, it does
// not know how to snapshot or restore anything. `undo.snapshot` is captured
// synchronously before `onConfirm` runs; `undo.restore(snap)` receives it
// back when the user clicks Undo.
// 157 §2 — the option bag, named so the context, the provider and the hook can
// all speak it without restating (or widening) a single field.
export type ConfirmOptions = {
  label?: any;
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onNo?: () => void;
  onConfirm: () => void;
  undo?: { snapshot: any; restore: (snap: any) => void } | null;
  toastName?: any;
  archive?: any;
  windowMs?: number;
  onExpire?: () => void;
  // 179 A.3b — the dialog's own stacking level. It renders at 300 by default,
  // which is under the call window (CallWindow.tsx, CALL_WINDOW_Z); a caller
  // inside a higher layer says so here. Widened, not worked around (rule 4).
  zIndex?: number;
};

/* 209 §25 (addendum 6) [Laurent] — « Isn't that backwards reasoning, the
   important undo rushed while the informative longer? » It was: the undo's
   window was 5 s while a toast now stays 10. The window goes to 10 s — the bar
   runs 10 s and the act commits (`onExpire`) 10 s after it happened. ONE
   constant, read by the window, the dialog's sentence and the bar (they read
   three copies of 5000), and by the three undo bars that keep a clock of their
   own (Dashboard's task purge and time-entry delete, CaseView's party removal).
   A caller that asks for longer keeps it (the conversions' 20 s, the time
   entries' 15 and 30). */
export const UNDO_WINDOW_MS = 10000;

export function useConfirmAndUndo() {
  const [confirmDlg, setConfirmDlg] = useState<any>(null);
  const [undoToast, setUndoToast] = useState<any>(null);
  const timerRef = useRef<any>(null);
  const idRef = useRef(0);
  // The pending commit hook. A ref, not state: it must be callable (and
  // clearable) synchronously from the timer, from a replacing action and from
  // the unmount cleanup, none of which can wait on a re-render.
  const pendingExpireRef = useRef<(() => void) | null>(null);
  const commitPending = () => {
    const fn = pendingExpireRef.current;
    pendingExpireRef.current = null;
    if (fn) fn();
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      // Auto-commit on unmount (contract above): with an onExpire attached,
      // committing means FIRING it, not dropping it.
      const fn = pendingExpireRef.current;
      pendingExpireRef.current = null;
      if (fn) fn();
    },
    [],
  );

  // 157 §2 — `onNo` / `cancelLabel` exist for the ONE shape the plain gate
  // could not carry: a two-outcome choice where declining is itself an action
  // (the collective-case orphan question — OK deletes the records, Cancel keeps
  // them as unlinked clients, and BOTH then commit the import). Without it,
  // moving that site off window.confirm would have made Cancel abandon the
  // whole import. Absent `onNo` the dialog behaves exactly as before.
  function confirmAndUndo({ label, title, message, confirmLabel, cancelLabel, onConfirm, onNo, undo, toastName, archive, windowMs, onExpire, zIndex }: ConfirmOptions) {
    if (isDev() && !label && !message && !undo) {
      throw new Error("confirmAndUndo: must supply label, message, undo, or some of them");
    }
    const runDelete = () => {
      const snap = undo ? undo.snapshot : null;
      onConfirm();
      if (undo) {
        if (timerRef.current) clearTimeout(timerRef.current);
        // Single-slot: the replaced action's reversal window closes early —
        // its commit hook fires now, before the new toast takes the slot.
        commitPending();
        idRef.current += 1;
        const ms = windowMs || UNDO_WINDOW_MS;
        pendingExpireRef.current = onExpire || null;
        setUndoToast({
          id: idRef.current,
          toastName,
          windowMs: ms,
          restore: () => undo.restore(snap),
        });
        timerRef.current = setTimeout(() => {
          setUndoToast(null);
          commitPending();
        }, ms);
      } else if (onExpire) {
        // No undo window at all: the commit point is immediate.
        onExpire();
      }
    };
    if (label || message) {
      setConfirmDlg({
        label,
        title,
        message,
        confirmLabel,
        cancelLabel,
        onNo,
        hasUndo: !!undo,
        archive: !!archive,
        // 144 §5 — the dialog body states the WINDOW, so it must know it. 140
        // §2 made windowMs per-call and the body sentence kept saying "5
        // seconds"; with a 30s caller that sentence would lie. Widened, not
        // worked around (rule 4).
        windowMs,
        zIndex, // 179 A.3b — carried to the dialog, absent means its default
        onYes: () => {
          setConfirmDlg(null);
          runDelete();
        },
      });
    } else {
      runDelete();
    }
  }

  const cancelConfirm = () => setConfirmDlg(null);
  const runUndo = () => {
    if (!undoToast) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    // Undone — the commit hook must never fire.
    pendingExpireRef.current = null;
    undoToast.restore();
    setUndoToast(null);
  };

  return { confirmAndUndo, confirmDlg, cancelConfirm, undoToast, runUndo };
}

// 157 §2 — THE APP'S ONE CONFIRM, MADE REACHABLE.
//
// `useConfirmAndUndo` above already WAS the app's confirm. What it was not was
// reachable: App owns the single instance and hands it down as a prop, so the
// fifteen files still calling window.confirm had no way to it that did not mean
// drilling a prop through trees with nothing to do with confirming. This is
// not a second confirm primitive — the disease this project exists to cure —
// it is the same instance App already renders, published on a context.
//
// The dialog still renders exactly once, from App, next to ConfirmDeleteDialog.
const ConfirmCtx = createContext<((opts: ConfirmOptions) => void) | null>(null);

export function ConfirmProvider({
  confirm,
  children,
}: {
  confirm: (opts: ConfirmOptions) => void;
  children: ReactNode;
}) {
  return <ConfirmCtx.Provider value={confirm}>{children}</ConfirmCtx.Provider>;
}

// FAILS CLOSED, deliberately. A confirm gate whose provider has gone missing
// must not let the destructive act through: in dev that is a thrown wiring bug
// (at call time, so a missing provider cannot white-screen a whole subtree on
// render), in production the action simply does not run.
export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (fn) return fn;
  return (_opts: ConfirmOptions) => {
    const msg = "useConfirm: no <ConfirmProvider> above this component";
    if (isDev()) throw new Error(msg);
    console.error(msg + " — action refused");
  };
}

export function ConfirmDeleteDialog({ dlg, onCancel }: { dlg?: any; onCancel?: () => void }) {
  const { t } = useTranslation();
  if (!dlg) return null;
  // 140 §2 — a `message` makes this a PROCEED dialog: the body is the given
  // sentence, the affirmative button carries `confirmLabel` and the save
  // styling (a forward action, not a destructive one). The delete/archive
  // arms below are byte-for-byte what they were.
  const proceed = !!dlg.message;
  // 179 A.3b — the dialog's stacking level, read through a typed view of the
  // bag so the `any` does not leak into the prop (the eslint ratchet counts
  // every unsafe read; this file may not rise).
  const dialogZ = (dlg as { zIndex?: number }).zIndex ?? Z.modal2;
  return (
    <ModalLayout
      title={
        dlg.title != null
          ? dlg.title
          : // 161B §5 — NO DIALOG CAN RENDER THE WORD "undefined". Both
            // label-derived titles interpolate `dlg.label`, and a call that
            // passes `message` with neither `title` nor `label` is legal (the
            // PROCEED shape, 140 §2) — it produced the heading "Delete
            // undefined?" over a body that was not about deleting anything.
            // The §5 census found TWENTY such call sites, so fixing only the
            // one that was reported would have left nineteen saying it.
            //
            // The guard is the narrowest that closes it: a dialog with a
            // label keeps its exact wording, a dialog with a title keeps its
            // own, and ONLY the label-less case — which had no correct
            // rendering to lose — falls to the neutral heading. Those sites
            // still deserve titles of their own; this is a floor, not the
            // fix, and the census in the commit is the list.
            dlg.label == null
            ? t("common.confirm", { fallback: "Confirm" })
            : dlg.archive
              ? t("confirm.archive_label", {
                  label: dlg.label,
                  fallback: `Archive ${dlg.label}?`,
                })
              : t("confirm.delete_label", {
                  label: dlg.label,
                  fallback: `Delete ${dlg.label}?`,
                })
      }
      showX={false}
      rawBody
      footer={null}
      zIndex={dialogZ}
      maxWidth={340}
    >
        <div style={{ padding: "16px 20px" }}>
          <p style={{ fontSize: FS.s11, color: "var(--tm)", marginBottom: 20 }}>
            {proceed
              ? dlg.message
              : dlg.archive
                ? t("confirm.archive_explained", {
                    fallback: "It moves to Archive and can be restored.",
                  })
                : dlg.hasUndo
                  ? // 144 §5 — the seconds follow the call's own window (the
                    // prior key's sentence hardcoded five; its rows stay
                    // seeded but nothing reads them any more).
                    t("confirm.removed_with_undo_s", withCount(Math.round((dlg.windowMs || UNDO_WINDOW_MS) / 1000), "seconds", { fallback: `This will be removed. You'll have ${Math.round((dlg.windowMs || UNDO_WINDOW_MS) / 1000)} seconds to undo.`,
                    }))
                  : t("confirm.permanent", {
                      fallback: "This action is permanent and cannot be undone.",
                    })}
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              alignItems: "stretch",
            }}
          >
            <button
              type="button"
              className="modal-cancel"
              onClick={() => {
                onCancel?.();
                // 157 §2 — declining is an ACTION at the one two-outcome site.
                dlg.onNo?.();
              }}
            >
              {dlg.cancelLabel || t("common.cancel", { fallback: "Cancel" })}
            </button>
            <button
              type="button"
              className={proceed ? "modal-save" : "modal-delete"}
              onClick={dlg.onYes}
            >
              {proceed
                ? dlg.confirmLabel || t("common.continue", { fallback: "Continue" })
                : dlg.archive
                  ? t("common.archive", { fallback: "Archive" })
                  : t("common.delete", { fallback: "Delete" })}
            </button>
          </div>
        </div>
    </ModalLayout>
  );
}

export function UndoToast({ toast, onUndo }: { toast?: any; onUndo?: () => void }) {
  const { t } = useTranslation();
  if (!toast) return null;
  return (
    <div
      key={toast.id}
      style={{
        position: "fixed",
        bottom: 16,
        left: "50%",
        transform: "translateX(-50%)",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 12px 11px",
        background: CT.c_f87171,
        borderRadius: RAD.r8,
        fontSize: FS.s11,
        zIndex: Z.popoverBackdrop,
        boxShadow: "0 6px 20px rgba(0,0,0,.4)",
        minWidth: 220,
      }}
    >
      <span style={{ flex: 1, color: CT.c_fff, fontSize: FS.s10_5, fontWeight: 500 }}>
        {toast.toastName}
      </span>
      <button
        type="button"
        style={{
          padding: "2px 10px",
          fontSize: FS.s10,
          background: "none",
          border: "1.5px solid rgba(255,255,255,.8)",
          color: CT.c_fff,
          borderRadius: RAD.r6,
          cursor: "pointer",
          fontWeight: 600,
          fontFamily: "inherit",
          flexShrink: 0,
        }}
        onClick={onUndo}
      >
        {t("common.undo", { fallback: "Undo" })}
      </button>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          height: 3,
          background: "rgba(255,255,255,.45)",
          // 140 §2 — the bar empties over the toast's OWN window, which is
          // per-call now (5s as written, 20s at the convert site).
          animation: `ud ${toast.windowMs || UNDO_WINDOW_MS}ms linear forwards`,
        }}
      />
    </div>
  );
}
